import { create } from 'zustand';
import { applyNodeChanges, applyEdgeChanges } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { getDefaultPages, getDefaultFileName } from './initialData';
import {
  fetchERDDocuments, fetchERDDocument, createERDDocument,
  updateERDDocument, deleteERDDocument, restoreERDDocument,
  fetchERDRevisions,
} from '../../services/api';

function updateCurrentPage(pages, currentPageId, updater) {
  return pages.map(p => p.id === currentPageId ? updater(p) : p);
}

let autoSaveTimer = null;

function scheduleAutoSave(get) {
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  autoSaveTimer = setTimeout(async () => {
    const state = get();
    if (state.isDirty && state.currentDocId && state.saveStatus !== 'saving') {
      try {
        await state.saveDocument();
      } catch (e) {
        console.error('Auto-save failed:', e);
      }
    }
  }, 5000);
}

function markDirty(set, get) {
  set({ isDirty: true, saveStatus: 'unsaved' });
  scheduleAutoSave(get);
}

export const useERDStore = create((set, get) => ({
  // Document list
  documents: [],
  documentsLoading: false,

  // Current document
  currentDocId: null,
  fileName: '',
  pages: [],
  currentPageId: '',
  selectedElementId: null,
  selectedElementType: null,
  isDirty: false,
  saveStatus: 'saved',
  snapToGrid: false,
  clipboardNode: null,

  // Revisions
  revisions: [],
  revisionsLoading: false,

  // Selectors
  currentPage: () => {
    const { pages, currentPageId } = get();
    return pages.find(p => p.id === currentPageId) || pages[0] || { id: '', name: '', nodes: [], edges: [] };
  },

  selectedNode: () => {
    const { selectedElementId, selectedElementType } = get();
    if (selectedElementType !== 'node' || !selectedElementId) return null;
    const page = get().currentPage();
    return page.nodes.find(n => n.id === selectedElementId) ?? null;
  },

  selectedEdge: () => {
    const { selectedElementId, selectedElementType } = get();
    if (selectedElementType !== 'edge' || !selectedElementId) return null;
    const page = get().currentPage();
    return page.edges.find(e => e.id === selectedElementId) ?? null;
  },

  // Document list operations
  loadDocuments: async () => {
    set({ documentsLoading: true });
    try {
      const docs = await fetchERDDocuments();
      set({ documents: docs, documentsLoading: false });
    } catch (err) {
      console.error('Failed to load ERD documents:', err);
      set({ documentsLoading: false });
    }
  },

  createDocument: async (name) => {
    try {
      const pages = getDefaultPages();
      const doc = await createERDDocument({ name: name || getDefaultFileName(), pages });
      set({
        currentDocId: doc.id,
        fileName: doc.name,
        pages: doc.pages,
        currentPageId: doc.pages[0]?.id || '',
        selectedElementId: null,
        selectedElementType: null,
        isDirty: false,
        saveStatus: 'saved',
      });
      return doc;
    } catch (err) {
      console.error('Failed to create document:', err);
      throw err;
    }
  },

  openDocument: async (id) => {
    try {
      const doc = await fetchERDDocument(id);
      set({
        currentDocId: doc.id,
        fileName: doc.name,
        pages: doc.pages || [],
        currentPageId: doc.pages?.[0]?.id || '',
        selectedElementId: null,
        selectedElementType: null,
        isDirty: false,
        saveStatus: 'saved',
      });
    } catch (err) {
      console.error('Failed to open document:', err);
      throw err;
    }
  },

  saveDocument: async () => {
    const { currentDocId, fileName, pages } = get();
    if (!currentDocId) return;
    set({ saveStatus: 'saving' });
    try {
      await updateERDDocument(currentDocId, { name: fileName, pages });
      set({ isDirty: false, saveStatus: 'saved' });
    } catch (err) {
      console.error('Failed to save document:', err);
      set({ saveStatus: 'error' });
      throw err;
    }
  },

  deleteDocument: async (id) => {
    try {
      await deleteERDDocument(id);
      const { documents, currentDocId } = get();
      const updated = documents.filter(d => d.id !== id);
      if (currentDocId === id) {
        set({ documents: updated, currentDocId: null, fileName: '', pages: [], currentPageId: '', isDirty: false });
      } else {
        set({ documents: updated });
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
      throw err;
    }
  },

  restoreDocument: async (id) => {
    try {
      await restoreERDDocument(id);
      await get().loadDocuments();
    } catch (err) {
      console.error('Failed to restore document:', err);
      throw err;
    }
  },

  closeDocument: () => {
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    set({
      currentDocId: null,
      fileName: '',
      pages: [],
      currentPageId: '',
      selectedElementId: null,
      selectedElementType: null,
      isDirty: false,
      saveStatus: 'saved',
      revisions: [],
    });
  },

  loadRevisions: async () => {
    const { currentDocId } = get();
    if (!currentDocId) return;
    set({ revisionsLoading: true });
    try {
      const revs = await fetchERDRevisions(currentDocId);
      set({ revisions: revs, revisionsLoading: false });
    } catch (err) {
      console.error('Failed to load revisions:', err);
      set({ revisionsLoading: false });
    }
  },

  restoreRevision: (revision) => {
    set({
      pages: revision.pages,
      currentPageId: revision.pages[0]?.id || '',
      selectedElementId: null,
      selectedElementType: null,
    });
    markDirty(set, get);
  },

  // Export/Import as .ferd.json
  exportToFile: () => {
    const { fileName, pages } = get();
    const data = {
      version: '1.0',
      name: fileName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      pages,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName.replace(/\s+/g, '-').toLowerCase()}.ferd.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  importFromFile: async (fileData) => {
    try {
      const doc = await createERDDocument({ name: fileData.name || 'Imported ERD', pages: fileData.pages });
      set({
        currentDocId: doc.id,
        fileName: doc.name,
        pages: doc.pages,
        currentPageId: doc.pages[0]?.id || '',
        selectedElementId: null,
        selectedElementType: null,
        isDirty: false,
        saveStatus: 'saved',
      });
      return doc;
    } catch (err) {
      console.error('Failed to import file:', err);
      throw err;
    }
  },

  // Page actions
  setCurrentPage: (id) => set({ currentPageId: id, selectedElementId: null, selectedElementType: null }),

  addPage: (name) => {
    const id = nanoid();
    const newPage = { id, name: name ?? `Page ${get().pages.length + 1}`, nodes: [], edges: [] };
    set(s => ({ pages: [...s.pages, newPage], currentPageId: id }));
    markDirty(set, get);
  },

  deletePage: (id) => {
    const { pages, currentPageId } = get();
    if (pages.length <= 1) return;
    const newPages = pages.filter(p => p.id !== id);
    const newCurrentId = currentPageId === id ? newPages[0].id : currentPageId;
    set({ pages: newPages, currentPageId: newCurrentId });
    markDirty(set, get);
  },

  renamePage: (id, name) => {
    set(s => ({ pages: s.pages.map(p => p.id === id ? { ...p, name } : p) }));
    markDirty(set, get);
  },

  // React Flow callbacks
  onNodesChange: (changes) => {
    const { pages, currentPageId } = get();
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({
        ...p, nodes: applyNodeChanges(changes, p.nodes),
      })),
    });
    markDirty(set, get);
  },

  onEdgesChange: (changes) => {
    const { pages, currentPageId } = get();
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({
        ...p, edges: applyEdgeChanges(changes, p.edges),
      })),
    });
    markDirty(set, get);
  },

  onConnect: (connection) => {
    const { pages, currentPageId } = get();
    const newEdge = {
      ...connection,
      id: nanoid(),
      type: 'relationship',
      data: { relationshipType: 'reference', label: '', description: '' },
      source: connection.source ?? '',
      target: connection.target ?? '',
    };
    set({
      pages: updateCurrentPage(pages, currentPageId, p => {
        const exists = p.edges.some(e =>
          e.source === newEdge.source &&
          e.target === newEdge.target &&
          (e.sourceHandle || null) === (newEdge.sourceHandle || null) &&
          (e.targetHandle || null) === (newEdge.targetHandle || null)
        );
        return { ...p, edges: exists ? p.edges : [...p.edges, newEdge] };
      }),
    });
    markDirty(set, get);
  },

  setViewport: (viewport) => {
    const { pages, currentPageId } = get();
    set({ pages: updateCurrentPage(pages, currentPageId, p => ({ ...p, viewport })) });
  },

  // Node actions
  addNode: (type, position) => {
    const { pages, currentPageId } = get();
    const id = nanoid();
    const isNote = type === 'note';
    const newNode = {
      id, type, position,
      data: {
        label: isNote ? 'Note' : 'new_collection',
        fields: isNote ? [] : [{ id: nanoid(), name: 'id', type: 'auto-id', required: true, isDocId: true }],
        description: '',
        subcollections: [],
      },
    };
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({ ...p, nodes: [...p.nodes, newNode] })),
      selectedElementId: id,
      selectedElementType: 'node',
    });
    markDirty(set, get);
  },

  updateNodeData: (id, data) => {
    const { pages, currentPageId } = get();
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({
        ...p, nodes: p.nodes.map(n => n.id === id ? { ...n, data: { ...n.data, ...data } } : n),
      })),
    });
    markDirty(set, get);
  },

  updateNodeType: (id, type) => {
    const { pages, currentPageId } = get();
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({
        ...p, nodes: p.nodes.map(n => n.id === id ? { ...n, type } : n),
      })),
    });
    markDirty(set, get);
  },

  deleteNode: (id) => {
    const { pages, currentPageId } = get();
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({
        ...p,
        nodes: p.nodes.filter(n => n.id !== id),
        edges: p.edges.filter(e => e.source !== id && e.target !== id),
      })),
      selectedElementId: null,
      selectedElementType: null,
    });
    markDirty(set, get);
  },

  duplicateNode: (id) => {
    const page = get().currentPage();
    const source = page.nodes.find(n => n.id === id);
    if (!source) return;
    const newId = nanoid();
    const newNode = {
      ...source,
      id: newId,
      position: { x: source.position.x + 40, y: source.position.y + 40 },
      data: {
        ...source.data,
        fields: (source.data.fields || []).map(f => ({ ...f, id: nanoid() })),
      },
    };
    const { pages, currentPageId } = get();
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({ ...p, nodes: [...p.nodes, newNode] })),
      selectedElementId: newId,
      selectedElementType: 'node',
    });
    markDirty(set, get);
  },

  copySelectedNode: () => {
    const page = get().currentPage();
    const { selectedElementId, selectedElementType } = get();
    if (selectedElementType !== 'node' || !selectedElementId) return;
    const source = page.nodes.find(n => n.id === selectedElementId);
    if (!source) return;
    const data = source.data || {};
    const template = {
      type: source.type,
      data: {
        ...data,
        fields: (data.fields || []).map(f => ({ ...f })), // ids regenerated on paste
        subcollections: [...(data.subcollections || [])],
      },
    };
    set({ clipboardNode: template });
  },

  pasteNode: () => {
    const { clipboardNode } = get();
    if (!clipboardNode) return;
    const page = get().currentPage();
    const { pages, currentPageId, selectedElementId } = get();

    let baseX = 0;
    let baseY = 0;
    if (selectedElementId) {
      const refNode = page.nodes.find(n => n.id === selectedElementId);
      if (refNode) {
        baseX = refNode.position.x;
        baseY = refNode.position.y;
      }
    }

    const id = nanoid();
    const data = clipboardNode.data || {};
    const newNode = {
      id,
      type: clipboardNode.type,
      position: { x: baseX + 40, y: baseY + 40 },
      data: {
        ...data,
        fields: (data.fields || []).map(f => ({ ...f, id: nanoid() })),
        subcollections: [...(data.subcollections || [])],
      },
    };

    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({
        ...p,
        nodes: [...p.nodes, newNode],
      })),
      selectedElementId: id,
      selectedElementType: 'node',
    });
    markDirty(set, get);
  },

  // Edge actions
  updateEdgeData: (id, data) => {
    const { pages, currentPageId } = get();
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({
        ...p, edges: p.edges.map(e => e.id === id ? { ...e, data: { ...e.data, ...data } } : e),
      })),
    });
    markDirty(set, get);
  },

  deleteEdge: (id) => {
    const { pages, currentPageId } = get();
    set({
      pages: updateCurrentPage(pages, currentPageId, p => ({
        ...p, edges: p.edges.filter(e => e.id !== id),
      })),
      selectedElementId: null,
      selectedElementType: null,
    });
    markDirty(set, get);
  },

  // Selection
  setSelection: (id, type) => set({ selectedElementId: id, selectedElementType: type }),

  // File name
  setFileName: (name) => {
    set({ fileName: name });
    markDirty(set, get);
  },

  toggleSnapToGrid: () => set(s => ({ snapToGrid: !s.snapToGrid })),
}));

export const selectNodes = (s) => s.pages.find(p => p.id === s.currentPageId)?.nodes ?? [];
export const selectEdges = (s) => s.pages.find(p => p.id === s.currentPageId)?.edges ?? [];
export const selectViewport = (s) => s.pages.find(p => p.id === s.currentPageId)?.viewport;
